"use client"
import { useEffect, useState } from 'react';
import Link from 'next/link';

// Data-protection consent banner (UAE PDPL / Dubai data-protection law).
// Shown on first visit to obtain the visitor's consent to store their data — including
// IP address — for enquiries, visit scheduling and bookings. The choice is persisted in
// localStorage so the banner is not shown again once answered.
//
// NOTE: this is the technical consent mechanism only. The exact wording and legal basis
// should be reviewed by the client's legal/DPO before going live.
const CONSENT_KEY = "hopo_data_consent";

type ConsentValue = "accepted" | "declined";

export function getStoredConsent(): ConsentValue | null {
    if (typeof window === "undefined") return null;
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === "accepted" || v === "declined" ? v : null;
}

const ConsentBanner = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Only show if the visitor hasn't answered yet.
        if (!getStoredConsent()) setVisible(true);
    }, []);

    const choose = (value: ConsentValue) => {
        try {
            window.localStorage.setItem(CONSENT_KEY, value);
            window.localStorage.setItem(`${CONSENT_KEY}_at`, new Date().toISOString());
        } catch {
            /* ignore storage errors (e.g. private mode) */
        }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-label="Data protection consent"
            aria-live="polite"
            className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4"
        >
            <div className="mx-auto max-w-4xl rounded-2xl border border-border bg-card/95 backdrop-blur shadow-xl p-4 sm:p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <p className="text-[13px] sm:text-sm leading-relaxed text-muted-foreground">
                        We collect and store certain data — including your <strong className="text-foreground">IP address</strong> — when
                        you browse HOPO Homes, submit an enquiry, schedule a visit or book a property, in line with the
                        UAE data-protection law. See our{" "}
                        <Link href="/privacy-policy" className="text-primary underline font-medium">Privacy Policy</Link>{" "}
                        for details. Do you consent to this?
                    </p>
                    <div className="flex shrink-0 gap-2">
                        <button
                            onClick={() => choose("declined")}
                            className="flex-1 md:flex-none rounded-full border border-border px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                        >
                            Decline
                        </button>
                        <button
                            onClick={() => choose("accepted")}
                            className="flex-1 md:flex-none rounded-full bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5"
                        >
                            Accept
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsentBanner;
